import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack
} from '@mui/material';
import {
  People,
  VideoLibrary,
  Visibility,
  Category,
  Notifications,
  TrendingUp,
  AccessTime
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { dashboardService } from '../services/api';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const currentLang = i18n.language;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getStats();
      setStats(response);
      setError(null);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {title}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              my: 1,
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            {value?.toLocaleString() || 0}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box
            sx={{
              bgcolor: `${color}.main`,
              color: 'white',
              borderRadius: 2,
              p: { xs: 1, sm: 1.5 },
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: { sm: 28, md: 32 } }} />
          </Box>
        )}
      </Box>
    </Paper>
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLang === 'gr' ? 'el-GR' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('dashboard.title')}
      </Typography>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.totalUsers')}
            value={stats?.stats?.users?.total}
            subtitle={`${stats?.stats?.users?.active || 0} ${t('dashboard.activeUsers').toLowerCase()}`}
            icon={People}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.totalVideos')}
            value={stats?.stats?.videos?.total}
            subtitle={`${stats?.stats?.videos?.active || 0} ${t('dashboard.activeVideos').toLowerCase()}`}
            icon={VideoLibrary}
            color="secondary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.videoViews')}
            value={stats?.stats?.videos?.views}
            subtitle={t('dashboard.last30Days')}
            icon={Visibility}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.totalStories')}
            value={stats?.stats?.stories?.total}
            subtitle={`${stats?.stats?.stories?.views || 0} ${t('dashboard.views')}`}
            icon={AccessTime}
            color="warning"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.categories')}
            value={stats?.stats?.categories?.total}
            icon={Category}
            color="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard.notificationsSent')}
            value={stats?.stats?.notifications?.sent}
            subtitle={t('dashboard.last30Days')}
            icon={Notifications}
            color="error"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp />
                {t('dashboard.mostViewedVideos')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('videos.titleField')}</TableCell>
                      <TableCell align="right">{t('videos.views')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats?.charts?.mostViewedVideos?.length > 0 ? (
                      stats.charts.mostViewedVideos.map((video, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {currentLang === 'gr' ? (video.titleGr || video.title) : (video.titleEn || video.title)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={video.viewCount} size="small" color="primary" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t('dashboard.noData')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dashboard.contentByCategory')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('categories.name')}</TableCell>
                      <TableCell align="right">{t('videos.title')}</TableCell>
                      <TableCell align="right">{t('stories.title')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats?.charts?.contentByCategory?.length > 0 ? (
                      stats.charts.contentByCategory.map((cat, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {currentLang === 'gr' ? cat.nameGr : cat.nameEn}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={cat.videos || 0} size="small" color="secondary" />
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={cat.stories || 0} size="small" color="warning" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t('dashboard.noData')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dashboard.recentVideos')}
              </Typography>
              <Stack spacing={2}>
                {stats?.recentActivity?.recentVideos?.length > 0 ? (
                  stats.recentActivity.recentVideos.map((video) => (
                    <Box
                      key={video._id}
                      sx={{
                        p: 2,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 0 }
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0, mr: { xs: 0, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {currentLang === 'gr' ? video.title : (video.titleEn || video.title)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatDate(video.createdAt)} • {video.category?.nameGr || video.category?.nameEn || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexShrink: 0,
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                        width: { xs: '100%', sm: 'auto' }
                      }}>
                        <Chip
                          label={video.status === 'active' ? t('videos.active') : t('videos.draft')}
                          size="small"
                          color={video.status === 'active' ? 'success' : 'default'}
                        />
                        <Chip
                          icon={<Visibility fontSize="small" />}
                          label={video.views || 0}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center">
                    {t('dashboard.noData')}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('dashboard.recentStories')}
              </Typography>
              <Stack spacing={2}>
                {stats?.recentActivity?.recentStories?.length > 0 ? (
                  stats.recentActivity.recentStories.map((story) => (
                    <Box
                      key={story._id}
                      sx={{
                        p: 2,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 0 }
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0, mr: { xs: 0, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {currentLang === 'gr' ? (story.title || story.titleGr) : (story.titleEn || story.title || story.titleGr)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatDate(story.createdAt)} • {story.category?.nameGr || story.category?.nameEn || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexShrink: 0,
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                        width: { xs: '100%', sm: 'auto' }
                      }}>
                        <Chip
                          label={story.mediaType === 'video' ? t('stories.video') : t('stories.image')}
                          size="small"
                          color={story.mediaType === 'video' ? 'error' : 'primary'}
                        />
                        <Chip
                          icon={<Visibility fontSize="small" />}
                          label={story.views || 0}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center">
                    {t('dashboard.noData')}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;